/* Humbler set builders (<humbler-builder>): "Buy any 2 for $99" (data-mode="pair") and
   "3 chains for $99" (data-mode="chains"). Selection with swap/remove, sessionStorage persistence,
   PDP deep links (?product= / ?chain= + &variant=), the optional $29 third piece (a 3-piece mode of the same
   grid), and the bag add with the live line properties. Set prices come from HumblerOffers.quoteSet
   (assets/humbler-offers.js). Markup: sections/humbler-bundle.liquid. Spec: docs/REBUILD-PLAN.md §3–§6. */
(() => {
  if (customElements.get('humbler-builder')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const desktop = window.matchMedia('(min-width: 1000px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isPreview = () => document.documentElement.dataset.humblerPreview === 'true';
  const localMoney = cents => {
    const value = Number(cents) / 100;
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  };
  const money = cents => (window.HumblerUI?.money || localMoney)(cents);
  // Offer prices (data-offer-price / data-third-price) are USD theme settings; prices shown and compared
  // with the shopper's product prices are converted with Shopify's rate (HumblerUI.fromUsd).
  const fromUsd = cents => (window.HumblerUI?.fromUsd ? window.HumblerUI.fromUsd(cents) : Number(cents) || 0);
  const moneyUsd = cents => (window.HumblerUI?.moneyUsd || localMoney)(cents);
  const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const decode = value => new DOMParser().parseFromString(`<!doctype html><body>${value ?? ''}`, 'text/html').body.textContent.trim();
  const inches = value => String(value ?? '').replace(/"/g, '″').replace(/\(\s*(.*?)\s*\)/, '($1)');
  const isLength = value => /^\d+(?:\.\d+)?\s*["″”]/.test(String(value ?? '').trim());
  const is22 = value => /^22\s*["″”]$/.test(String(value ?? '').trim());
  const visible = element => !!element && element.getClientRects().length > 0;
  const uuid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };

  class HumblerBuilder extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.mode = this.dataset.mode === 'chains' ? 'chains' : 'pair';
      this.size = Number(this.dataset.size) || (this.mode === 'chains' ? 3 : 2);
      this.offerPriceUsd = Number(this.dataset.offerPrice) || 9900;
      this.thirdPriceUsd = Number(this.dataset.thirdPrice) || 0;
      // 3 chains: each chain at the converted $33 (the set price over the count), as checkout charges it; never a
      // converted $99, a cent apart in some currencies.
      this.chainPrice = this.mode === 'chains' ? fromUsd(Math.round(this.offerPriceUsd / this.size)) : 0;
      this.offerPrice = this.mode === 'chains' ? this.chainPrice * this.size : fromUsd(this.offerPriceUsd);
      this.thirdPrice = fromUsd(this.thirdPriceUsd);
      this.giftTitle = decode(this.dataset.giftTitle || '');
      this.giftValue = Number(this.dataset.giftValue) || 0;
      this.giftThreshold = fromUsd(Number(this.dataset.giftThreshold) || 11500);
      this.flash = '';
      this.unlocking = false;
      this.strings = this.readJson('[data-hb-strings]', {});
      this.saveLine = decode(this.dataset.saveLine || '') || this.localSaveLine();
      this.querySelectorAll('[data-hb-save-line]').forEach(node => { node.textContent = this.saveLine; node.hidden = !this.saveLine; });
      this.products = new Map();
      for (const product of this.readJson('[data-hb-products]', [])) {
        if (!product?.h || !Array.isArray(product.v) || !product.v.length) continue;
        product.t = decode(product.t);
        product.n = decode(product.n || product.t);
        product.x = product.x ? String(product.x) : ''; // a listed variant the store prices at $29 as a 3rd piece
        product.k = String(product.k ?? '') === '1'; // ships with the two included chains (snippets/humbler-includes-chains)
        this.products.set(product.h, product);
      }
      // Theme setting "Any 2-for-$99 piece can be the third piece": every grid piece, in any of its variants.
      this.thirdAny = this.mode === 'pair' && this.dataset.thirdAny === 'true';
      this.selection = [];
      this.replacing = null;
      // The 3-piece offer: `three` once "Add a 3rd" is tapped (the set shows a 3rd slot and the grid picks it),
      // `third` the piece in that slot. Back to 2 turns both off.
      this.three = false;
      this.third = null;
      this.category = this.querySelector('[data-hb-tab][aria-pressed="true"]')?.dataset.hbTab || '';
      this.finish = 'all';
      this.summary = this.querySelector('[data-hb-summary]');
      this.slots = this.querySelector('[data-hb-slots]');
      this.sheet = this.querySelector('[data-hb-sheet]');
      this.offerDialog = this.querySelector('[data-hb-offer]');
      this.icons = this.querySelector('template[data-hb-icons]')?.content;

      this.restore();
      this.sheet?.addEventListener('close', () => this.onSheetClosed());
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.onChange(event));
      window.addEventListener('pageshow', event => { if (event.persisted) this.reenter(); });
      window.addEventListener('popstate', () => this.reenter());
      document.addEventListener('humbler:cart:updated', event => this.onCartUpdated(event));
      desktop.addEventListener('change', () => this.setExpanded(false));
      if (this.mode === 'chains') this.querySelectorAll('[data-hb-style]').forEach(card => this.syncStyle(card));
      // Intent (hover, touch, focus) starts loading photos that are about to be shown, so they open on a
      // photo rather than an empty stage: a chain style card's other finishes (hidden lazy frames), a
      // category tab's first cards (hidden items), the pieces that can be the $29 3rd ("Add a 3rd").
      const warmImages = (images, limit) => {
        if (window.HumblerSkeleton) window.HumblerSkeleton.warm(images, limit);
        else [...images].slice(0, limit).forEach(img => { img.loading = 'eager'; });
      };
      const cardImages = items => items.flatMap(item => [...item.querySelectorAll('.hb-card__image:not(.hb-card__image--alt)')]);
      const warmIntent = event => {
        const target = event.target.closest?.('[data-hb-style], [data-hb-tab], [data-hb-third-start]');
        if (!target || target.hbWarm) return;
        target.hbWarm = true;
        if (target.matches('[data-hb-style]')) warmImages(target.querySelectorAll('[data-hb-style-image] img'), 8);
        else if (target.matches('[data-hb-tab]')) {
          warmImages(cardImages([...this.querySelectorAll('[data-hb-item]')].filter(item => item.dataset.category === target.dataset.hbTab)), 4);
        } else warmImages(cardImages([...this.querySelectorAll('[data-hb-item]')].filter(item => this.canBeThird(item.dataset.hbItem))), 6);
      };
      ['pointerover', 'focusin', 'touchstart'].forEach(type => this.addEventListener(type, warmIntent, { passive: true }));
      this.consumeSeed();
      this.render();
      this.dataset.ready = 'true';
    }

    /* In another currency Liquid can't compare product prices with the USD offer price, so the
       "Save up to" line is worked out here from the regular-price range (data-regular-min/max). */
    localSaveLine() {
      if (!window.HumblerUI?.localized) return '';
      const max = Number(this.dataset.regularMax) || 0;
      const min = Number(this.dataset.regularMin) || 0;
      if (!max || max <= this.offerPrice) return '';
      if (this.mode === 'chains') return this.t('save_range', { min: money(min - this.offerPrice), max: money(max - this.offerPrice) });
      return this.t('save_up_to', { amount: money(max - this.offerPrice) });
    }

    readJson(selector, fallback) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || 'null') ?? fallback; } catch { return fallback; }
    }

    t(key, values = {}) {
      return String(this.strings[key] ?? '').replace(/\{(\w+)\}|%(\w+)%/g, (match, brace, pct) => (values[brace || pct] ?? match));
    }

    icon(name) {
      return this.icons?.querySelector(`[data-icon="${name}"]`)?.innerHTML || '';
    }

    /* ---------- data helpers ---------- */
    variant(product, id) { return product?.v.find(variant => String(variant.id) === String(id)) || null; }
    firstAvailable(product) { return product?.v.find(variant => variant.a) || null; }
    preferredVariant(product) {
      return product?.v.find(variant => variant.a && variant.o.some(is22)) || this.firstAvailable(product);
    }
    finishLabel(product) { return product?.f ? this.t(`finish_${product.f}`) : ''; }
    kindLabel(product) { return this.t(this.mode === 'chains' ? 'kind_chains' : `kind_${product?.c}`); }
    /* Tray meta line: "PENDANT · GOLD · +2 CHAINS" (category clears up shared names; the chains only for a pendant that
       ships with them). */
    variantLabel(product, variant) {
      const parts = [this.kindLabel(product), this.finishLabel(product)];
      if (variant && variant.t !== 'Default Title') parts.push(inches(variant.t));
      if (this.mode === 'pair' && product?.k) parts.push(this.t('with_chains'));
      return parts.filter(Boolean).join(' · ');
    }
    itemOf(entry) {
      const product = this.products.get(entry.handle);
      return { product, variant: this.variant(product, entry.variantId) };
    }

    /* 2 for $99: the offer engine prices the set as the bag and checkout will (shopper's currency; the settings amounts
       are converted). Its 2-for pieces are this grid's; three chains are the chain deal's $99 (data-chain-handles), as the
       store's function prices them with no code. */
    offerConfig() {
      if (!this.engineConfig) {
        const pieces = [...this.products.values()];
        const chainHandles = this.dataset.chainEnabled === 'false' ? [] : (this.dataset.chainHandles || '').split(',').filter(Boolean);
        const usd = window.HumblerOffers.normalizeConfig({ chains: { price: Number(this.dataset.chainPrice) || undefined, handles: chainHandles } });
        this.engineConfig = window.HumblerOffers.normalizeConfig({
          pair: { price: this.offerPrice, handles: pieces.map(product => product.h) },
          chains: { price: fromUsd(usd.chains.price), unitPrice: fromUsd(usd.chains.unitPrice), handles: usd.chains.handles },
          third: { price: this.thirdPrice, variants: pieces.filter(product => product.x).map(product => ({ handle: product.h, variantId: product.x })), any: this.thirdAny },
          gift: { threshold: this.giftThreshold }
        });
      }
      return this.engineConfig;
    }

    engineQuote(third) {
      if (this.mode !== 'pair' || typeof window.HumblerOffers?.quoteSet !== 'function') return null;
      const pieces = this.selection.map(entry => ({ handle: entry.handle, variantId: entry.variantId, price: this.itemOf(entry).variant?.p || 0 }));
      return window.HumblerOffers.quoteSet(pieces, third ? { handle: third.handle, variantId: third.variantId, price: third.price } : null, this.offerConfig());
    }

    quote() {
      const complete = this.selection.length === this.size;
      const set = this.engineQuote(null);
      if (set) return { regular: set.regular, complete, price: set.price, savings: set.savings, discounted: complete && set.discounted, thirdOpen: complete && set.thirdOpen };
      // 3 chains: every chain at most a chain's price ($33) once the set is complete; a page without the engine: the set
      // price caps the regular total.
      const prices = this.selection.map(entry => this.itemOf(entry).variant?.p || 0);
      const regular = prices.reduce((sum, value) => sum + value, 0);
      const capped = this.mode === 'chains' ? prices.reduce((sum, value) => sum + Math.min(value, this.chainPrice), 0) : Math.min(regular, this.offerPrice);
      const price = complete ? capped : regular;
      const discounted = complete && price < regular;
      return { regular, complete, price, savings: regular - price, discounted, thirdOpen: discounted };
    }

    /* What the bag will charge for this add: the set plus the $29 third piece once one is in the 3rd slot. */
    bagQuote(quote = this.quote()) {
      const third = this.three && this.third && this.thirdEligible(quote) ? this.third : null;
      if (!third) return { ...quote, third: false, gift: false };
      const set = this.engineQuote(third);
      if (set) return { ...quote, third: set.third, regular: set.regular, price: set.price, savings: set.savings, gift: set.gift };
      const regular = quote.regular + third.price;
      const price = quote.price + Math.min(third.price || this.thirdPrice, this.thirdPrice);
      return { ...quote, third: true, regular, price, savings: regular - price, gift: price >= this.giftThreshold };
    }

    /* The set earns the $29 3rd piece: a discounted pair whose $29 add reaches the free gift (offer rule 3). */
    thirdEligible(quote = this.quote()) {
      return this.mode === 'pair' && !!quote.thirdOpen && [...this.products.values()].some(product => this.canBeThird(product.h));
    }

    /* A grid piece that can be the 3rd (theme settings › Humbler offers): any piece in stock when "any" is on,
       otherwise one whose listed variant is; and, while the offer promises the free gift, one that reaches it. */
    canBeThird(handle) {
      const product = this.products.get(handle);
      if (!product) return false;
      if (!(this.thirdAny ? product.v.some(variant => variant.a) : !!this.variant(product, product.x)?.a)) return false;
      // A piece's sizes and lengths share its price, so its first in-stock one stands for them.
      const variant = this.thirdAny ? product.v.find(entry => entry.a) : this.variant(product, product.x);
      return this.reachesGift({ handle: product.h, variantId: String(variant.id), price: variant.p });
    }

    /* The 3-piece offer's copy promises the free gift ("A 3rd piece unlocks a free cross pendant"), so with a gift in it
       a piece is the 3rd only when it takes this set to the gift at the store's price: a 3rd chain after two chains makes
       the $99 chain deal (it adds nothing, no gift) and is left out of the offer. Worked out once per set and piece. */
    reachesGift(piece) {
      if (!this.giftTitle || !piece || this.selection.length !== this.size) return true;
      const set = this.selection.map(entry => entry.variantId).join('|');
      if (this.reachFor !== set) { this.reachFor = set; this.reach = new Map(); }
      const key = `${piece.variantId}:${piece.price}`;
      if (!this.reach.has(key)) {
        const quote = this.engineQuote(piece);
        this.reach.set(key, quote ? quote.gift : this.quote().price + Math.min(piece.price, this.thirdPrice) >= this.giftThreshold);
      }
      return this.reach.get(key);
    }

    /* The 3rd piece in one variant: the listed one, or with "any" the variant asked for (a piece with one variant
       needs none). Null when that variant can't be it, or a size/length still has to be chosen. */
    thirdPick(handle, variantId) {
      const product = this.products.get(handle);
      if (!product) return null;
      const id = this.thirdAny ? variantId || (product.v.length === 1 ? product.v[0].id : '') : product.x;
      const variant = id ? this.variant(product, id) : null;
      if (!variant?.a || (!this.thirdAny && String(variant.id) !== product.x)) return null;
      const size = variant.t && variant.t !== 'Default Title' ? inches(variant.t) : '';
      return { handle: product.h, variantId: String(variant.id), title: product.t, name: product.n, image: variant.i || product.i || '', finish: product.f || '', size, price: variant.p };
    }

    /* Picking the 3rd: the grid shows only the pieces that can be it (not while a set piece is being swapped). */
    picking() {
      return this.mode === 'pair' && this.three && this.replacing === null;
    }

    /* ---------- persistence ---------- */
    restore() {
      let saved = null;
      try { saved = JSON.parse(window.sessionStorage.getItem(this.dataset.storageKey) || 'null'); } catch { saved = null; }
      const items = saved?.version === 1 && Array.isArray(saved.items) ? saved.items : [];
      this.selection = [];
      for (const entry of items) {
        if (this.selection.length >= this.size) break;
        const product = this.products.get(entry?.handle);
        const variant = this.variant(product, entry?.variantId);
        if (variant?.a) this.selection.push({ handle: product.h, variantId: String(variant.id) });
      }
      const third = this.thirdPick(saved?.third?.handle, saved?.third?.variantId);
      this.third = third && third.variantId === String(saved.third.variantId) ? third : null;
      this.three = this.mode === 'pair' && (saved?.three === true || !!this.third);
      this.pruneThird();
    }

    save() {
      try {
        window.sessionStorage.setItem(this.dataset.storageKey, JSON.stringify({
          version: 1,
          items: this.selection,
          three: this.three,
          third: this.third ? { handle: this.third.handle, variantId: this.third.variantId } : null
        }));
      } catch { /* storage unavailable: the set lives for this page view only */ }
    }

    /* The 3-piece offer needs a set that earns it; a 3rd piece is never also one of the set's two, and leaves the slot
       once a swap means it no longer takes the set to the free gift (reachesGift). */
    pruneThird() {
      if (!this.three && !this.third) return;
      if (!this.thirdEligible()) { this.three = false; this.third = null; return; }
      if (this.third && (this.selection.some(entry => entry.handle === this.third.handle) || !this.reachesGift(this.third))) this.third = null;
    }

    commit({ keepNote = false } = {}) {
      this.pruneThird();
      this.save();
      if (!keepNote) { this.note(''); this.clearFlash(); }
      this.render();
    }

    reenter() {
      this.restore();
      this.replacing = null;
      this.closeDialog(this.sheet);
      this.consumeSeed();
      this.render();
    }

    /* ---------- deep links from the product page ---------- */
    consumeSeed() {
      let url;
      try { url = new URL(window.location.href); } catch { return; }
      const key = (this.mode === 'chains' ? ['chain', 'product'] : ['product', 'chain']).find(name => url.searchParams.has(name));
      if (!key) return;
      const handle = url.searchParams.get(key);
      const variantId = url.searchParams.get('variant');
      ['chain', 'product', 'variant'].forEach(name => url.searchParams.delete(name));
      try { window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`); } catch { /* ignore */ }

      const product = this.products.get(handle);
      if (!product) { this.flashMsg(this.t('unavailable'), 4000); return; }
      if (this.mode === 'pair' && product.c) { this.category = product.c; this.finish = 'all'; }
      let variant = this.variant(product, variantId);
      const full = this.selection.length >= this.size;
      if (!variant && !variantId) {
        if (this.mode === 'pair' && product.v.length > 1) {
          // The piece chosen on the product page always lands: a full set swaps out its last piece.
          if (full) { this.replacing = this.size - 1; this.seedSwap = true; }
          setTimeout(() => this.openSheet(handle), 0);
          return;
        }
        variant = this.preferredVariant(product);
      }
      if (!variant?.a) { this.flashMsg(this.t('unavailable'), 4000); return; }
      if (this.mode === 'chains') this.selectStyle(product, variant);
      if (this.selection.some(entry => entry.handle === handle && entry.variantId === String(variant.id))) { this.flashMsg(this.t('already')); return; }
      if (full) { this.replacing = this.size - 1; this.seedSwap = true; }
      // The piece from the product page lands with a short confirmation on the tray's savings line.
      if (this.add(handle, variant.id) && !full) this.flashMsg(this.t('seed_added', { title: product.t }));
    }

    /* ---------- selection ---------- */
    add(handle, variantId) {
      const product = this.products.get(handle);
      const variant = this.variant(product, variantId);
      if (!variant?.a) { this.flashMsg(this.t('unavailable'), 4000); return false; }
      const entry = { handle, variantId: String(variant.id) };
      const swapping = this.replacing !== null;
      if (swapping) this.selection[this.replacing] = entry;
      else if (this.selection.length < this.size) this.selection.push(entry);
      else { this.full(); return false; }
      this.replacing = null;
      this.confirmed = false;
      const seeded = swapping && this.seedSwap;
      this.seedSwap = false;
      this.commit();
      if (seeded) this.flashMsg(this.t('swapped_in', { title: product.t }));
      else this.announce(this.t(swapping ? 'replaced_piece' : 'added_piece', { title: product.n, count: this.selection.length, total: this.size }));
      return true;
    }

    removeAt(index) {
      const entry = this.selection[index];
      if (!entry) return false;
      const { product } = this.itemOf(entry);
      this.selection.splice(index, 1);
      if (this.replacing === index) this.replacing = null;
      else if (this.replacing !== null && this.replacing > index) this.replacing -= 1;
      // A set piece leaves a 3-piece set: the 3rd piece moves up into the pair and the 3rd slot opens again.
      if (this.three && this.third && this.products.has(this.third.handle)) {
        this.selection.push({ handle: this.third.handle, variantId: this.third.variantId });
        this.third = null;
      }
      this.commit();
      this.announce(this.t('removed_piece', { title: product?.t || '' }));
      return true;
    }

    full() {
      this.setExpanded(true);
      this.flashMsg(this.t('full'), 4000);
    }

    clearSelection() {
      this.selection = [];
      this.three = false;
      this.third = null;
      this.replacing = null;
      this.save();
    }

    /* ---------- the 3-piece offer ---------- */
    /* "Add a 3rd": the offer becomes 2 for $99 + a 3rd for $29 and the shopper picks the 3rd in the grid. */
    startThird() {
      if (!this.thirdEligible()) return;
      this.three = true;
      this.replacing = null;
      this.commit();
      this.setExpanded(false);
      this.revealGrid();
      this.announce(this.t('third_pick'));
      this.querySelector('[data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled)')?.focus({ preventScroll: true });
    }

    /* Back to 2: the 3rd slot and any piece in it go; the set stays. */
    stopThird() {
      if (!this.three) return;
      this.three = false;
      this.third = null;
      this.commit();
      this.announce(this.t('third_back', { price: money(this.offerPrice) }));
    }

    pickThird(piece) {
      if (!piece) return;
      const first = !this.third;
      this.third = piece;
      this.unlocking = first;
      this.commit();
      this.unlocking = false;
      const added = this.t('added_piece', { title: piece.name, count: this.size + 1, total: this.size + 1 });
      // The collapsed phone tray has no room for the unlocked panel: its savings line says it for a moment.
      const unlocked = first && this.giftTitle && this.bagQuote().gift ? this.t('unlocked_flash', { gift: this.giftTitle }) : '';
      if (unlocked) this.flashMsg(unlocked, 4000, true);
      this.announce(unlocked ? `${added} ${unlocked}` : added);
    }

    /* The $29 3rd-piece pop-up: once per visit, when a 2-piece set that earns the 3rd goes to the bag. Accept picks the
       3rd in the grid; decline adds the 2. */
    offerDue(quote = this.quote()) {
      if (!this.offerDialog || this.three || this.offerSeen || !this.thirdEligible(quote)) return false;
      try { return !window.sessionStorage.getItem(`${this.dataset.storageKey}:third-offer`); } catch { return true; }
    }

    offerThird(trigger) {
      this.offerSeen = true;
      try { window.sessionStorage.setItem(`${this.dataset.storageKey}:third-offer`, '1'); } catch { /* once per page view then */ }
      this.offerTrigger = trigger;
      this.offerOpenedAt = performance.now();
      const decline = this.offerDialog.querySelector('[data-hb-offer-decline]');
      const percent = this.offerPercent();
      if (decline) decline.textContent = percent > 0 ? this.t('offer_decline', { percent }) : this.t('offer_decline_plain', { price: money(this.offerPrice) });
      this.openDialog(this.offerDialog, trigger);
    }

    /* The decline's "up to X% off": the most the 3rd-piece price takes off a piece it can be, from the pieces' own
       prices (rounded down, so it never overstates). */
    offerPercent() {
      let best = 0;
      for (const product of this.products.values()) {
        if (this.selection.some(entry => entry.handle === product.h) || !this.canBeThird(product.h)) continue;
        const variants = this.thirdAny ? product.v.filter(variant => variant.a) : [this.variant(product, product.x)];
        for (const variant of variants) if (variant.p > this.thirdPrice) best = Math.max(best, Math.floor(((variant.p - this.thirdPrice) / variant.p) * 100));
      }
      return best;
    }

    removeThird() {
      const title = this.third?.name || '';
      this.third = null;
      this.commit();
      this.announce(this.t('removed_piece', { title }));
    }

    /* ---------- rendering ---------- */
    render() {
      const quote = this.quote();
      const bag = this.bagQuote(quote);
      this.renderSlots(bag);
      this.renderMath(quote, bag);
      this.renderThird(quote, bag);
      this.renderThirdBar();
      const count = this.querySelector('[data-hb-count]');
      if (count) count.textContent = this.t('count', { count: this.selection.length + (this.three && this.third ? 1 : 0), total: this.size + (this.three ? 1 : 0) });
      this.renderAdd(quote, bag);
      const expand = this.querySelector('[data-hb-expand]');
      if (expand) expand.hidden = this.selection.length === 0;
      if (!this.selection.length) this.setExpanded(false);
      this.dataset.count = String(this.selection.length);
      this.toggleAttribute('data-swapping', this.replacing !== null);
      this.toggleAttribute('data-three', this.three);
      this.toggleAttribute('data-picking', this.picking());
      if (this.mode === 'pair') this.renderGrid();
      else this.renderStyles();
    }

    /* The bag button is never a dead end: until the set is complete it says what's missing and,
       when tapped, brings the catalog into view and points at the first empty slot. */
    addState(quote = this.quote()) {
      // Picking the 3rd is optional: the set of 2 can go to the bag meanwhile.
      if (quote.complete && this.replacing === null) return 'ready';
      if (!this.selection.length && this.confirmed) return 'again';
      return 'pending';
    }

    renderAdd(quote, bag) {
      const add = this.querySelector('[data-hb-add]');
      if (!add) return;
      const state = this.addState(quote);
      const filled = this.selection.length;
      add.dataset.state = state;
      add.disabled = !!this.busy;
      add.classList.toggle('hb-button--secondary', state !== 'ready');
      let label;
      if (state === 'ready') label = `${esc(this.t('add_set'))}<span class="hb-button__sep" aria-hidden="true"></span><span class="hb-button__price">${esc(money(bag.price))}</span>`;
      else if (state === 'again') label = esc(this.t('cta_again'));
      else if (this.replacing !== null) label = esc(this.t('cta_swap'));
      else if (filled) label = esc(this.t('cta_more', { count: this.size - filled }));
      else label = esc(this.t('cta_start'));
      add.querySelector('[data-hb-add-label]').innerHTML = label;
    }

    renderSlots(bag = this.bagQuote()) {
      if (!this.slots) return;
      const rows = [];
      const close = this.icon('close');
      for (let index = 0; index < this.size; index += 1) {
        const entry = this.selection[index];
        if (!entry) {
          rows.push(`<li class="hb-builder__slot is-empty"><span class="hb-builder__slot-media" aria-hidden="true"><span class="hb-builder__slot-n">${this.icon('plus')}</span></span><span class="hb-builder__slot-info"><span class="hb-builder__slot-line"><span class="hb-builder__slot-title hb-muted">${esc(this.t('choose'))}</span></span></span></li>`);
          continue;
        }
        const { product, variant } = this.itemOf(entry);
        const swapping = this.replacing === index;
        const title = product?.t || '';
        const name = product?.n || title;
        const image = variant?.i || product?.i || '';
        rows.push(`<li class="hb-builder__slot${swapping ? ' is-swapping' : ''}">
          <span class="hb-builder__slot-media hb-stage">${image ? `<img src="${esc(image)}" alt="" width="300" height="375" loading="lazy">` : ''}</span>
          <span class="hb-builder__slot-info">
            <span class="hb-builder__slot-line"><span class="hb-builder__slot-title" title="${esc(name)}">${esc(title)}</span><span class="hb-builder__slot-price">${esc(money(variant?.p || 0))}</span></span>
            <span class="hb-builder__slot-meta hb-mono hb-mono--micro">${esc(this.variantLabel(product, variant))}</span>
          </span>
          <span class="hb-builder__slot-actions">
            <button type="button" class="hb-builder__slot-action" data-hb-swap="${index}" aria-pressed="${swapping}" aria-label="${esc(swapping ? this.t('cancel_swap') : this.t('swap_named', { title: name }))}">${this.icon('swap')}</button>
            <button type="button" class="hb-builder__slot-action" data-hb-remove="${index}" aria-label="${esc(this.t('remove_named', { title: name }))}">${close}</button>
          </span>
        </li>`);
      }
      // The 3-piece offer adds a 3rd slot (a small "+$29" thumb in the collapsed tray): empty until a piece is picked
      // in the grid, then that piece at its $29 price. Its × goes back to 2 while empty, else empties the slot.
      if (this.three) {
        const third = this.third;
        const finish = third?.finish ? this.t(`finish_${third.finish}`) : '';
        const badge = `<span class="hb-builder__slot-badge" aria-hidden="true">+${esc(money(this.thirdPrice))}</span>`;
        let price = esc(money(third ? third.price : this.thirdPrice));
        const cost = third && bag.third ? this.thirdCost(third) : null;
        if (cost !== null && cost < third.price) price = `<s class="hb-builder__slot-was"><span class="hb-sr">${esc(this.t('regular'))}</span>${price}</s> ${esc(money(cost))}`;
        rows.push(`<li class="hb-builder__slot hb-builder__slot--third${third ? '' : ' is-empty'}">
          ${third
            ? `<span class="hb-builder__slot-media hb-stage">${third.image ? `<img src="${esc(third.image)}" alt="" width="300" height="375" loading="lazy">` : ''}${badge}</span>`
            : `<span class="hb-builder__slot-media" aria-hidden="true"><span class="hb-builder__slot-n">${this.icon('plus')}</span>${badge}</span>`}
          <span class="hb-builder__slot-info">
            <span class="hb-builder__slot-line"><span class="hb-builder__slot-title${third ? '' : ' hb-muted'}"${third ? ` title="${esc(third.name)}"` : ''}>${esc(third ? third.title : this.t('third_empty'))}</span><span class="hb-builder__slot-price">${price}</span></span>
            <span class="hb-builder__slot-meta hb-mono hb-mono--micro">${esc([this.t('third_label'), finish, third?.size].filter(Boolean).join(' · '))}</span>
          </span>
          <span class="hb-builder__slot-actions">
            ${third
              ? `<button type="button" class="hb-builder__slot-action" data-hb-third-remove aria-label="${esc(this.t('third_remove'))}">${close}</button>`
              : `<button type="button" class="hb-builder__slot-action" data-hb-third-back aria-label="${esc(this.t('third_back', { price: money(this.offerPrice) }))}">${close}</button>`}
          </span>
        </li>`);
      }
      // Keyed by position + markup: unchanged rows keep their DOM, so their photos never blink on a tap.
      const list = this.slots;
      rows.forEach((row, index) => {
        const current = list.children[index];
        if (current && current.hbHtml === row) return;
        const template = document.createElement('template');
        template.innerHTML = row.trim();
        const fresh = template.content.firstElementChild;
        fresh.hbHtml = row;
        if (current) current.replaceWith(fresh);
        else list.append(fresh);
      });
      while (list.children.length > rows.length) list.lastElementChild.remove();
    }

    renderMath(quote = this.quote(), bag = this.bagQuote(quote)) {
      const box = this.querySelector('[data-hb-math]');
      if (!box) return;
      const filled = this.selection.length;
      const price = value => `<strong class="hb-builder__math-price">${esc(money(value))}</strong>`;
      const arrow = '<span class="hb-builder__math-arrow" aria-hidden="true">→</span>';
      const label = text => `<span class="hb-muted hb-builder__math-label">${esc(text)}</span>`;
      // Collapsed phone tray: the optional third piece is one gold chip on the savings line. It glints only the
      // first time it is drawn (is-new), not on every re-render, and again once the offer comes back.
      const offerThird = this.thirdEligible(quote) && !this.three;
      if (!offerThird) this.thirdChipSeen = false;
      const thirdButton = offerThird
        ? `<button type="button" class="hb-builder__third-inline${this.thirdChipSeen ? '' : ' is-new'}" data-hb-third-start>${esc(this.t('third_short', { price: money(this.thirdPrice) }))}</button>`
        : '';
      let chip = false;
      let main;
      let sub = '';
      if (!filled && this.confirmed) {
        const count = Number.isFinite(this.bagCount) && this.bagCount > 0 ? ` (${this.bagCount})` : '';
        main = `<span class="hb-builder__math-ok">${this.icon('check')}${esc(this.t('set_added'))}</span>`;
        sub = `<a class="hb-builder__math-link" href="${esc(`${root}cart`)}" data-hb-view-bag>${esc(this.t('view_bag'))}${esc(count)}</a>`;
      } else if (!filled) {
        main = `<span class="hb-builder__math-target">${esc(this.t('target', { count: this.size }))}</span> ${arrow} ${price(this.offerPrice)}`;
        if (this.saveLine) sub = `<span class="hb-sale-text">${esc(this.saveLine)}</span>`;
      } else if (!quote.complete) {
        main = `${price(quote.regular)} <span class="hb-muted">${esc(this.t('so_far'))}</span>`;
        sub = `<span class="hb-muted">${esc(this.t('pick_more_cap', { count: this.size - filled, price: money(this.offerPrice) }))}</span>`;
      } else if (quote.discounted) {
        const saveText = bag.gift && this.giftTitle
          ? this.t('you_save_gift', { amount: money(bag.savings), gift: this.giftTitle })
          : this.t('you_save', { amount: money(bag.savings) });
        // The set's price, as the bag button adds it. Picking the 3rd: what a 3rd piece unlocks (green, a gain), or
        // where to pick it; the $29 is on the 3rd slot and the grid's tags.
        main = `${label(this.t('regular'))} <s class="hb-builder__math-was">${esc(money(bag.regular))}</s> ${arrow} ${price(bag.price)}`;
        if (this.three && !this.third) {
          sub = this.giftTitle
            ? `<span class="hb-builder__math-gift">${this.icon('gift')}${esc(this.t('third_unlocks', { gift: this.giftTitle }))}</span>`
            : `<span class="hb-muted">${esc(this.t('third_pick'))}</span>`;
        } else {
          sub = `<span class="hb-sale-text">${esc(saveText)}</span>${thirdButton}`;
          chip = !!thirdButton;
        }
      } else {
        main = `${label(this.t('regular'))} ${price(quote.price)}`;
        sub = `<span class="hb-muted">${esc(this.t('under_offer', { price: money(this.offerPrice) }))}</span>`;
      }
      if (this.replacing !== null) {
        const { product } = this.itemOf(this.selection[this.replacing] || {});
        sub = `<span>${esc(this.t('swapping', { title: product?.t || '' }))}</span>`;
        chip = false;
      }
      if (this.flash) {
        sub = this.flashOk
          ? `<span class="hb-builder__math-flash hb-builder__math-flash--ok">${this.icon('check')}${esc(this.flash)}</span>`
          : `<span class="hb-builder__math-flash">${esc(this.flash)}</span>`;
        chip = false;
      }
      if (chip) this.thirdChipSeen = true;
      box.innerHTML = `<p class="hb-builder__math-main">${main}</p>${sub ? `<p class="hb-builder__math-sub">${sub}</p>` : ''}`;
    }

    flashMsg(message, ms = 3000, ok = false) {
      if (!message) return;
      this.flash = message;
      this.flashOk = ok;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = ''; this.renderMath(); }, ms);
      this.renderMath();
      this.announce(message);
    }

    clearFlash() {
      if (!this.flash) return;
      this.flash = '';
      clearTimeout(this.flashTimer);
    }

    renderThird(quote, bag = this.bagQuote(quote)) {
      const box = this.querySelector('[data-hb-third]');
      if (!box) return;
      // Only the offer lives here; once it is on, the 3rd slot is a row in the set (renderSlots), and once a piece
      // fills it the "Offer unlocked" panel shows (it pops in only right after a pick, not on restore).
      const eligible = this.thirdEligible(quote);
      box.hidden = !eligible || this.three;
      const done = this.querySelector('[data-hb-third-done]');
      if (done) {
        // With the gift in it, the panel says "Free cross pendant": only once the set really gets it (bag.gift).
        const show = eligible && bag.third && (!this.giftTitle || bag.gift);
        const appearing = show && done.hidden;
        done.hidden = !show;
        if (appearing && this.unlocking) this.celebrate(done);
      }
      let from = 0;
      for (const product of this.products.values()) {
        // With any piece the lowest price would be a cheap bracelet's, not what the 3rd-piece price beats: left out.
        if (this.thirdAny || this.selection.some(entry => entry.handle === product.h) || !this.canBeThird(product.h)) continue;
        const price = this.variant(product, product.x).p;
        if (!from || price < from) from = price;
      }
      // "from $55": the lowest regular price of the choices still open, struck only while the 3rd-piece price beats it.
      const was = box.querySelector('[data-hb-third-was]');
      if (was) {
        was.hidden = !(from > this.thirdPrice);
        const amount = was.querySelector('[data-hb-third-from]');
        if (amount && from) amount.textContent = money(from);
      }
    }

    /* The notice over the grid while the 3-piece offer is on: what to tap, and the way back to 2. */
    renderThirdBar() {
      const bar = this.querySelector('[data-hb-third-bar]');
      if (!bar) return;
      bar.hidden = !this.picking();
      const text = bar.querySelector('[data-hb-third-bar-text]');
      if (text) text.textContent = this.third ? this.t('third_switch') : this.t('third_pick');
      bar.querySelector('[data-hb-third-back]')?.setAttribute('aria-label', this.t('third_back', { price: money(this.offerPrice) }));
    }

    celebrate(panel) {
      if (reduceMotion.matches || typeof panel.animate !== 'function') return;
      const ease = 'cubic-bezier(.2, .7, .2, 1)';
      panel.animate([{ opacity: 0, transform: 'translateY(6px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: 320, delay: 120, easing: ease, fill: 'backwards' });
      panel.querySelector('.hb-builder__unlocked-icon')?.animate(
        [{ transform: 'scale(.4) rotate(-14deg)' }, { transform: 'scale(1.14) rotate(5deg)', offset: .6 }, { transform: 'none' }],
        { duration: 560, delay: 220, easing: ease, fill: 'backwards' }
      );
    }

    renderGrid() {
      const handles = this.selection.map(entry => entry.handle);
      const third = this.three ? this.third?.handle : null;
      const picking = this.picking();
      for (const button of this.querySelectorAll('[data-hb-toggle]')) {
        const handle = button.dataset.hbToggle;
        const selected = handles.includes(handle) || handle === third;
        button.setAttribute('aria-pressed', String(selected));
        const item = button.closest('[data-hb-item]');
        item?.classList.toggle('is-selected', selected);
        item?.classList.toggle('is-third', handle === third);
        // While picking the 3rd, a piece that can be it is described by its "3rd · $29" tag.
        const tag = item?.querySelector('.hb-builder__third-tag');
        if (tag && picking) button.setAttribute('aria-describedby', tag.id);
        else button.removeAttribute('aria-describedby');
      }
      if (picking) this.renderThirdTags();
      this.applyFilter();
    }

    /* What a piece adds as the 3rd, to the cent as checkout prices it (assets/humbler-offers.js): $29 after a $99 set,
       its own price when that is lower (a $27 bracelet), nothing for a 3rd chain that makes the $99 chain deal. */
    thirdCost(piece) {
      const set = this.engineQuote(piece);
      return set ? set.price - this.quote().price : Math.min(piece.price, this.thirdPrice);
    }

    /* Each tag says what that piece adds with this set (worked out again only when the set changes). */
    renderThirdTags() {
      const key = this.selection.map(entry => entry.variantId).join('|');
      if (this.thirdTagsFor === key) return;
      this.thirdTagsFor = key;
      for (const tag of this.querySelectorAll('.hb-builder__third-tag')) {
        const product = this.products.get(tag.closest('[data-hb-item]')?.dataset.hbItem);
        const variant = product && (this.thirdPick(product.h) || product.v.filter(entry => entry.a).sort((a, b) => a.p - b.p)[0]);
        if (!variant || !tag.firstElementChild) continue;
        const piece = variant.handle ? variant : { handle: product.h, variantId: String(variant.id), price: variant.p };
        tag.firstElementChild.textContent = this.t('third_tag', { price: money(this.thirdCost(piece)) });
      }
    }

    applyFilter() {
      // Picking the 3rd: only the pieces that can be it (the set's own two can't), and only the tabs that have some.
      const picking = this.picking();
      const items = [...this.querySelectorAll('[data-hb-item]')];
      const inSet = new Set(this.selection.map(entry => entry.handle));
      const eligible = item => !picking || (!inSet.has(item.dataset.hbItem) && this.canBeThird(item.dataset.hbItem));
      const counts = new Map();
      for (const item of items) if (eligible(item)) counts.set(item.dataset.category, (counts.get(item.dataset.category) || 0) + 1);
      const tabs = [...this.querySelectorAll('[data-hb-tab]')];
      for (const tab of tabs) {
        tab.hidden = picking && !counts.has(tab.dataset.hbTab);
        const count = tab.querySelector('.hb-builder__tab-count');
        if (!count) continue;
        if (count.dataset.all === undefined) count.dataset.all = count.textContent;
        count.textContent = picking ? String(counts.get(tab.dataset.hbTab) || 0) : count.dataset.all;
      }
      if (picking && !counts.has(this.category)) this.category = tabs.find(tab => !tab.hidden)?.dataset.hbTab || this.category;
      let visible = 0;
      for (const item of items) {
        const show = (!this.category || item.dataset.category === this.category) && (this.finish === 'all' || item.dataset.finish === this.finish) && eligible(item);
        item.hidden = !show;
        if (show) visible += 1;
      }
      const empty = this.querySelector('[data-hb-empty]');
      if (empty) empty.hidden = visible > 0;
      // "Just chains? Any 3 for $99" only on the Chains tab.
      this.querySelectorAll('[data-hb-xsell]').forEach(link => { link.hidden = link.dataset.hbXsell !== this.category; });
      this.querySelectorAll('[data-hb-tab]').forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.hbTab === this.category)));
      this.querySelectorAll('[data-hb-finish]').forEach(chip => chip.setAttribute('aria-pressed', String(chip.dataset.hbFinish === this.finish)));
    }

    renderStyles() {
      for (const card of this.querySelectorAll('[data-hb-style]')) {
        const count = this.selection.filter(entry => this.products.get(entry.handle)?.s === card.dataset.hbStyle).length;
        card.classList.toggle('is-selected', count > 0);
        const badge = card.querySelector('[data-hb-style-count]');
        if (badge) {
          badge.hidden = count === 0;
          badge.textContent = count > 1 ? this.t('in_set_count', { count }) : this.t('in_set');
        }
        // "Added ✓" for a moment after a tap, then "Add another" while this style is in the set.
        const justAdded = card.hasAttribute('data-just-added') && this.replacing === null;
        card.querySelector('[data-hb-style-add]')?.classList.toggle('is-added', justAdded);
        const label = card.querySelector('[data-hb-style-add-label]');
        if (label) label.textContent = this.t(this.replacing !== null ? 'swap_in' : justAdded ? 'added_short' : count > 0 ? 'add_another' : 'add_to_set');
      }
    }

    confirmStyle(card) {
      card.setAttribute('data-just-added', '');
      clearTimeout(card.hbAddedTimer);
      card.hbAddedTimer = setTimeout(() => { card.removeAttribute('data-just-added'); this.renderStyles(); }, 1200);
      this.renderStyles();
    }

    /* ---------- chain style cards ---------- */
    styleProduct(card) {
      return this.products.get(card.querySelector('[data-hb-style-finish]:checked')?.value);
    }

    styleVariant(card) {
      const product = this.styleProduct(card);
      const length = card.querySelector('[data-hb-style-length]:checked')?.value;
      return product?.v.find(variant => variant.o.includes(length) || variant.t === length) || null;
    }

    syncStyle(card) {
      const product = this.styleProduct(card);
      if (!product) return;
      card.querySelectorAll('[data-hb-style-image]').forEach(frame => { frame.hidden = frame.dataset.hbStyleImage !== product.h; });
      const lengths = [...card.querySelectorAll('[data-hb-style-length]')];
      for (const input of lengths) {
        const variant = product.v.find(item => item.o.includes(input.value) || item.t === input.value);
        input.disabled = !variant?.a;
      }
      const checked = lengths.find(input => input.checked);
      if (!checked || checked.disabled) {
        const fallback = lengths.find(input => !input.disabled);
        if (fallback) fallback.checked = true;
      }
      const variant = this.styleVariant(card);
      const price = card.querySelector('[data-hb-style-price] .hb-price__current');
      if (price) price.textContent = money((variant || product.v[0]).p);
      const add = card.querySelector('[data-hb-style-add]');
      if (add) add.disabled = !variant?.a;
    }

    selectStyle(product, variant) {
      const card = [...this.querySelectorAll('[data-hb-style]')].find(item => item.dataset.hbStyle === product.s);
      if (!card) return;
      const finish = [...card.querySelectorAll('[data-hb-style-finish]')].find(input => input.value === product.h);
      if (finish) finish.checked = true;
      const option = variant.o[0] ?? variant.t;
      const length = [...card.querySelectorAll('[data-hb-style-length]')].find(input => input.value === option);
      if (length) length.checked = true;
      this.syncStyle(card);
    }

    /* ---------- variant sheet (2 for $99) ---------- */
    openSheet(handle, trigger, variantId) {
      const product = this.products.get(handle);
      const body = this.sheet?.querySelector('[data-hb-sheet-body]');
      if (!product || !body) return;
      this.sheetHandle = handle;
      this.sheetTrigger = trigger || null;
      const chosen = this.variant(product, variantId) || this.preferredVariant(product) || product.v[0];
      const names = (product.o || []).map(name => (typeof name === 'string' ? name : name?.name)).filter(Boolean);
      let lengths = names.length > 0;
      const groups = names.map((name, index) => {
        const values = [...new Set(product.v.map(variant => variant.o[index]).filter(value => value !== undefined && value !== null))];
        const lengthGroup = values.every(isLength);
        if (!lengthGroup) lengths = false;
        const legend = lengthGroup && /^size$/i.test(name) ? this.t('length') : name;
        const chips = values.map(value => `<label class="hb-builder__sheet-chip"><input class="hb-sr" type="radio" name="hb-sheet-option-${index}" value="${esc(value)}" data-hb-sheet-option="${index}" ${chosen.o[index] === value ? 'checked' : ''}><span class="hb-chip">${esc(inches(value))}</span></label>`).join('');
        return `<fieldset class="hb-builder__sheet-group"><legend class="hb-mono hb-muted">${esc(legend)}</legend><div class="hb-builder__sheet-chips">${chips}</div></fieldset>`;
      }).join('');
      const title = this.sheet.querySelector('.hb-dialog__title');
      if (title) title.textContent = this.t(lengths ? 'sheet_length' : 'sheet_size');
      const finish = this.finishLabel(product);
      body.innerHTML = `
        <div class="hb-builder__sheet-product">
          <span class="hb-builder__sheet-thumb hb-stage"><img data-hb-sheet-image src="${esc(chosen.i || product.i || '')}" alt="" width="300" height="375"></span>
          <span class="hb-builder__sheet-meta">
            <span class="hb-builder__sheet-name">${esc(product.t)}</span>
            ${product.f ? `<span class="hb-builder__sheet-finish hb-mono hb-mono--micro hb-muted"><span class="hb-swatch hb-swatch--${esc(product.f)}" aria-hidden="true"></span>${esc(finish)}</span>` : ''}
            <span class="hb-price" data-hb-sheet-price></span>
          </span>
        </div>
        ${groups}`;
      this.updateSheet();
      this.openDialog(this.sheet, trigger);
      setTimeout(() => (body.querySelector('input:checked:not(:disabled)') || this.sheet.querySelector('[data-hb-sheet-add]'))?.focus({ preventScroll: true }), 0);
    }

    sheetVariant() {
      const product = this.products.get(this.sheetHandle);
      if (!product) return null;
      const picked = [...this.sheet.querySelectorAll('[data-hb-sheet-option]:checked')].reduce((map, input) => ({ ...map, [input.dataset.hbSheetOption]: input.value }), {});
      return product.v.find(variant => Object.entries(picked).every(([index, value]) => variant.o[Number(index)] === value)) || null;
    }

    updateSheet() {
      const product = this.products.get(this.sheetHandle);
      if (!product || !this.sheet) return;
      const picked = [...this.sheet.querySelectorAll('[data-hb-sheet-option]:checked')].reduce((map, input) => ({ ...map, [input.dataset.hbSheetOption]: input.value }), {});
      for (const input of this.sheet.querySelectorAll('[data-hb-sheet-option]')) {
        const index = Number(input.dataset.hbSheetOption);
        const match = product.v.find(variant => variant.o[index] === input.value && Object.entries(picked).every(([other, value]) => Number(other) === index || variant.o[Number(other)] === value));
        input.closest('label')?.querySelector('.hb-chip')?.classList.toggle('is-unavailable', !match?.a);
      }
      const variant = this.sheetVariant();
      // Picking the 3rd: the piece is shown at what it adds as the 3rd (thirdCost, as its grid tag and the 3rd slot say),
      // its own price struck.
      const piece = this.picking() && variant ? { handle: product.h, variantId: String(variant.id), price: variant.p } : null;
      const third = piece ? this.thirdCost(piece) : null;
      const price = this.sheet.querySelector('[data-hb-sheet-price]');
      if (price) {
        const was = third !== null ? (third < variant.p ? variant.p : 0) : variant && variant.cp > variant.p ? variant.cp : 0;
        price.className = was ? 'hb-price hb-price--sale' : 'hb-price';
        price.innerHTML = variant ? `<span class="hb-price__current">${esc(money(third ?? variant.p))}</span>${was ? `<s class="hb-price__compare">${esc(money(was))}</s>` : ''}` : '';
      }
      const image = this.sheet.querySelector('[data-hb-sheet-image]');
      const source = variant?.i || product.i;
      // The previous variant's photo never lingers: the thumb shimmers until the new one is in.
      if (image && source && image.getAttribute('src') !== source) {
        if (window.HumblerSkeleton) window.HumblerSkeleton.swapSrc(image, source);
        else image.src = source;
      }
      const add = this.sheet.querySelector('[data-hb-sheet-add]');
      if (add) {
        add.disabled = !variant?.a || (!!piece && !this.reachesGift(piece));
        add.textContent = !variant?.a ? this.t('sold_out')
          : this.picking() ? this.t('third_sheet_add', { price: money(third) })
            : this.t(this.replacing !== null ? 'swap_in' : 'add_to_set');
      }
    }

    /* ---------- dialogs, notes, tray ---------- */
    openDialog(dialog, trigger) {
      if (!dialog) return;
      if (window.HumblerUI?.openDialog) window.HumblerUI.openDialog(dialog, trigger);
      else if (!dialog.open) dialog.showModal();
    }

    closeDialog(dialog) {
      if (!dialog?.open) return;
      if (window.HumblerUI?.closeDialog) window.HumblerUI.closeDialog(dialog);
      else dialog.close();
    }

    announce(message) {
      const live = this.querySelector('[data-hb-live]');
      if (!live) return;
      live.textContent = '';
      setTimeout(() => { live.textContent = message; }, 60);
    }

    note(message, tone = '', html = '') {
      const box = this.querySelector('[data-hb-note]');
      if (!box) return;
      box.hidden = !message;
      box.dataset.tone = tone;
      box.innerHTML = message ? `${tone === 'ok' ? this.icon('check') : ''}<span>${esc(message)}</span>${html}` : '';
      if (message) this.announce(message);
    }

    setExpanded(open) {
      const button = this.querySelector('[data-hb-expand]');
      const expanded = !!open && !desktop.matches && this.selection.length > 0;
      this.summary?.toggleAttribute('data-expanded', expanded);
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      const label = button.querySelector('[data-hb-expand-label]');
      if (label) label.textContent = this.t(expanded ? 'hide_set' : 'edit_set');
    }

    /* Bring the catalog into view only when none of it is on screen (e.g. from the reels below). */
    revealCatalog() {
      const items = [...this.querySelectorAll(this.mode === 'chains' ? '[data-hb-style]' : '[data-hb-item]:not([hidden])')];
      const toolbar = this.querySelector('.hb-builder__toolbar');
      const top = toolbar ? toolbar.getBoundingClientRect().bottom : 0;
      const bottom = !desktop.matches && this.summary ? this.summary.getBoundingClientRect().top : window.innerHeight;
      const onScreen = items.some(item => {
        const rect = item.getBoundingClientRect();
        return rect.bottom > top + 48 && rect.top < bottom - 48;
      });
      if (onScreen) return;
      if (this.mode === 'pair') { this.revealGrid(); return; }
      const styles = this.querySelector('.hb-builder__styles');
      if (!styles) return;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hb-header-height')) || 0;
      window.scrollTo({ top: Math.max(0, window.scrollY + styles.getBoundingClientRect().top - header - 12), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    /* Tapping the bag button before the set is complete: point at what's missing. */
    cue() {
      this.revealCatalog();
      const slot = this.slots?.querySelector('.hb-builder__slot.is-empty');
      if (slot) {
        slot.classList.remove('is-cue');
        void slot.offsetWidth;
        slot.classList.add('is-cue');
        setTimeout(() => slot.classList.remove('is-cue'), 1400);
      }
      if (this.replacing === null) this.flashMsg(this.picking() ? this.t('third_pick') : this.t('cue'));
      this.querySelector('[data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled), [data-hb-style-add]:not(:disabled)')?.focus({ preventScroll: true });
    }

    scrollToCatalog() {
      const target = this.querySelector('.hb-builder__toolbar, .hb-builder__styles');
      if (!target || desktop.matches) return;
      const top = target.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) target.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    /* After a tab or finish change, bring the first row up under the sticky toolbar. */
    revealGrid() {
      const grid = this.querySelector('[data-hb-grid]');
      const toolbar = this.querySelector('.hb-builder__toolbar');
      if (!grid || !toolbar) return;
      // Where the toolbar's bottom edge sits once it is stuck (it may have scrolled away with the catalog).
      const toolbarBottom = (parseFloat(getComputedStyle(toolbar).top) || 0) + toolbar.offsetHeight;
      // The Chains tab's cross-link sits above the grid; keep it in view too.
      const first = [...this.querySelectorAll('[data-hb-xsell]')].find(link => !link.hidden) || grid;
      const gridTop = first.getBoundingClientRect().top;
      if (gridTop >= toolbarBottom - 1 && gridTop < window.innerHeight * 0.6) return; // already in view below the toolbar
      const top = window.scrollY + gridTop - toolbarBottom - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }

    focusAfterSheet(trigger) {
      // HumblerUI restores focus on the dialog's close event; do it here too so focus never lands on <body>.
      setTimeout(() => {
        const target = trigger && document.contains(trigger) && visible(trigger) && !trigger.disabled
          ? trigger
          : this.querySelector('[data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled)');
        if (document.activeElement === document.body || !this.contains(document.activeElement)) target?.focus({ preventScroll: true });
      }, 0);
    }

    onSheetClosed() {
      // A deep link into a full set waits on the sheet; dismissing it leaves the set as it was.
      if (!this.seedSwap) return;
      this.seedSwap = false;
      this.replacing = null;
      this.render();
    }

    /* ---------- bag ---------- */
    async addSet(button) {
      const quote = this.quote();
      if (this.addState(quote) !== 'ready' || this.busy) return;
      const bundleId = uuid();
      const label = this.dataset.offerLabel;
      // Line properties exactly as live sends them (scripts/data/live-offers.json).
      const items = this.selection.map((entry, index) => ({
        id: Number(entry.variantId),
        quantity: 1,
        properties: this.mode === 'chains'
          ? { _bundle_offer: label, _three_chain_offer: '3-for-99' }
          : { _bundle_id: bundleId, _bundle_offer: label, _bundle_item: `${index + 1} of ${this.size}` }
      }));
      // The $29 third piece is its own add after the set lands (HumblerCart adds the live _bundle_upsell properties and
      // checks the bag total rose by $29 at most). It carries the set's _bundle_id, so the store prices it as this
      // set's own 3rd and the bag shows it in this set's card.
      const third = this.bagQuote(quote).third ? { id: Number(this.third.variantId), quantity: 1, properties: { _bundle_id: bundleId } } : null;
      const options = { open: !third, source: this.mode === 'chains' ? 'chains' : 'builder' };
      if (this.mode === 'chains' && this.dataset.discountCode) options.discountCode = this.dataset.discountCode;

      this.busy = true;
      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      try {
        if (window.HumblerCart?.add) {
          const result = await window.HumblerCart.add(items, options);
          if (!result?.ok) {
            this.note(result?.error || this.t('error'), 'error');
          } else {
            let thirdError = '';
            if (third) {
              const extra = await window.HumblerCart.add([third], { source: 'third', open: true });
              if (!extra?.ok) thirdError = extra?.error || this.t('third_error', { price: money(this.thirdPrice) });
            }
            this.added(thirdError);
          }
        } else if (isPreview()) {
          this.note(this.t('preview_note'), 'info');
        } else {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: third
                ? [...items, { ...third, properties: { ...third.properties, _bundle_upsell: moneyUsd(this.thirdPriceUsd), _bundle_upsell_cents: String(this.thirdPriceUsd) } }]
                : items
            })
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || this.t('error'));
          this.clearSelection();
          const cartUrl = `${root}cart`;
          window.location.href = options.discountCode
            ? `${root}discount/${encodeURIComponent(options.discountCode)}?redirect=${encodeURIComponent(cartUrl)}`
            : cartUrl;
        }
      } catch (error) {
        this.note(error?.message || this.t('error'), 'error');
      } finally {
        this.busy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.render();
      }
    }

    added(thirdError = '') {
      this.clearSelection();
      this.confirmed = true;
      this.clearFlash();
      this.setExpanded(false);
      // "✓ Set added · View cart" replaces the savings line (renderMath); only a failed 3rd piece adds a note.
      if (thirdError) this.note(thirdError, 'error');
      this.announce(thirdError ? `${this.t('added')} ${thirdError}` : this.t('added'));
    }

    onCartUpdated(event) {
      const count = Number(event.detail?.item_count);
      if (Number.isFinite(count)) this.bagCount = count;
      if (this.confirmed && !this.selection.length) this.renderMath();
    }

    /* ---------- events ---------- */
    onClick(event) {
      const target = event.target;
      if (!window.HumblerUI && (target.closest('[data-hb-close]') || (target instanceof HTMLDialogElement && target.classList.contains('hb-dialog')))) {
        this.closeDialog(target.closest('dialog'));
        return;
      }
      const toggle = target.closest('[data-hb-toggle]');
      if (toggle) { this.onToggle(toggle); return; }

      const tab = target.closest('[data-hb-tab]');
      if (tab) { this.category = tab.dataset.hbTab; this.applyFilter(); this.revealGrid(); return; }

      const chip = target.closest('[data-hb-finish]');
      if (chip) { this.finish = chip.dataset.hbFinish; this.applyFilter(); this.revealGrid(); return; }

      const styleAdd = target.closest('[data-hb-style-add]');
      if (styleAdd) {
        const card = styleAdd.closest('[data-hb-style]');
        const product = this.styleProduct(card);
        const variant = this.styleVariant(card);
        if (product && variant && this.add(product.h, variant.id)) this.confirmStyle(card);
        return;
      }

      if (target.closest('[data-hb-sheet-add]')) {
        const variant = this.sheetVariant();
        // Picking the 3rd: the chosen size or length fills the 3rd slot; otherwise the piece joins the set.
        const pick = this.picking() ? this.thirdPick(this.sheetHandle, variant?.id) : null;
        const piece = pick && this.reachesGift(pick) ? pick : null;
        if (piece) this.pickThird(piece);
        if (piece || (!this.picking() && variant?.a && this.add(this.sheetHandle, variant.id))) {
          const trigger = this.sheetTrigger;
          this.closeDialog(this.sheet);
          this.focusAfterSheet(trigger);
        }
        return;
      }

      const swap = target.closest('[data-hb-swap]');
      if (swap) {
        const index = Number(swap.dataset.hbSwap);
        this.replacing = this.replacing === index ? null : index;
        this.commit();
        if (this.replacing !== null) {
          const { product } = this.itemOf(this.selection[index]);
          this.announce(this.t('swapping', { title: product?.n || '' }));
          this.setExpanded(false);
          this.scrollToCatalog();
          this.querySelector('[data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled), [data-hb-style-add]:not(:disabled)')?.focus({ preventScroll: true });
        } else {
          this.slots.querySelector(`[data-hb-swap="${index}"]`)?.focus({ preventScroll: true });
        }
        return;
      }

      const remove = target.closest('[data-hb-remove]');
      if (remove) {
        const index = Number(remove.dataset.hbRemove);
        if (this.removeAt(index)) {
          const next = this.slots.querySelector(`[data-hb-remove="${Math.min(index, this.selection.length - 1)}"]`)
            || this.querySelector('[data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled), [data-hb-style-add]:not(:disabled)');
          next?.focus({ preventScroll: true });
        }
        return;
      }

      const expand = target.closest('[data-hb-expand]');
      if (expand) { this.setExpanded(expand.getAttribute('aria-expanded') !== 'true'); return; }

      const add = target.closest('[data-hb-add]');
      if (add) {
        const state = this.addState();
        if (state === 'ready') {
          if (this.offerDue()) this.offerThird(add);
          else this.addSet(add);
        }
        else if (state === 'again') { this.confirmed = false; this.render(); this.cue(); }
        else this.cue();
        return;
      }

      if (target.closest('[data-hb-third-start]')) { this.startThird(); return; }

      // A quick second tap on the bag button lands in the pop-up while it slides in (its buttons sit where the bag button
      // was): its choices wait 400 ms, so only a real choice uses up the once-per-visit offer.
      if (target.closest('[data-hb-offer-accept], [data-hb-offer-decline]') && performance.now() - (this.offerOpenedAt || 0) < 400) return;
      // The $29 pop-up: accept picks the 3rd in the grid once the dialog has closed (HumblerUI hands focus back to the
      // bag button on close; picking then moves it to the grid); decline adds the 2.
      if (target.closest('[data-hb-offer-accept]')) {
        let started = false;
        const start = () => { if (!started) { started = true; this.startThird(); } };
        this.offerDialog.addEventListener('close', start, { once: true });
        this.closeDialog(this.offerDialog);
        setTimeout(start, 150); // a browser that never fires the dialog's close event
        return;
      }
      if (target.closest('[data-hb-offer-decline]')) {
        this.closeDialog(this.offerDialog);
        const add = this.offerTrigger || this.querySelector('[data-hb-add]');
        if (add) this.addSet(add);
        return;
      }

      if (target.closest('[data-hb-third-back]')) {
        this.stopThird();
        [...this.querySelectorAll('[data-hb-third-start], [data-hb-add]')].find(visible)?.focus({ preventScroll: true });
        return;
      }

      if (target.closest('[data-hb-third-remove]')) {
        this.removeThird();
        [...this.querySelectorAll('[data-hb-third-back], [data-hb-item]:not([hidden]) [data-hb-toggle]:not(:disabled)')].find(visible)?.focus({ preventScroll: true });
        return;
      }

      const viewBag = target.closest('[data-hb-view-bag]');
      if (viewBag && window.HumblerCart?.open && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        window.HumblerCart.open(viewBag);
      }
    }

    onToggle(button) {
      const handle = button.dataset.hbToggle;
      const product = this.products.get(handle);
      if (!product) return;
      const index = this.selection.map(entry => entry.handle).lastIndexOf(handle);
      if (index > -1) { this.removeAt(index); return; }
      // Picking the 3rd: a tap fills the 3rd slot, another piece switches it, the same piece empties it.
      if (this.picking()) {
        const piece = this.thirdPick(handle);
        if (this.third?.handle === handle) this.removeThird();
        else if (piece && this.reachesGift(piece)) this.pickThird(piece);
        else if (this.canBeThird(handle)) this.openSheet(handle, button); // its size or length first
        return;
      }
      if (this.replacing === null && this.selection.length >= this.size) { this.full(); return; }
      if (product.v.length === 1) this.add(handle, product.v[0].id);
      else this.openSheet(handle, button);
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-hb-style-finish], [data-hb-style-length]')) {
        this.syncStyle(target.closest('[data-hb-style]'));
      } else if (target.matches('[data-hb-sheet-option]')) {
        this.updateSheet();
      }
    }
  }

  customElements.define('humbler-builder', HumblerBuilder);
})();
